<template>
  <main class="account-recovery-page">
    <section class="recovery-card" aria-labelledby="recovery-title">
      <router-link to="/" class="brand-link" aria-label="RabiiTrip トップへ">
        <img src="/favicon.png" alt="" />
        <span>RabiiTrip</span>
      </router-link>

      <h1 id="recovery-title">アカウント復元</h1>

      <template v-if="hasRecoveryToken">
        <p class="description">
          削除したアカウントを復元します。復元後は通常どおりログインできます。
        </p>
        <button
          type="button"
          class="primary-button"
          :disabled="isSubmitting || isComplete"
          @click="restoreAccount"
        >
          {{ isSubmitting ? "復元中..." : isComplete ? "復元しました" : "アカウントを復元" }}
        </button>
      </template>

      <form v-else @submit.prevent="requestRecovery">
        <p class="description">
          登録したメールアドレスを入力してください。復元可能な場合に確認メールを送信します。
        </p>
        <label for="recovery-email">メールアドレス</label>
        <input
          id="recovery-email"
          v-model.trim="email"
          type="email"
          autocomplete="email"
          required
        />
        <button type="submit" class="primary-button" :disabled="isSubmitting">
          {{ isSubmitting ? "送信中..." : "復元メールを送信" }}
        </button>
      </form>

      <p v-if="successMessage" class="status success" role="status">
        {{ successMessage }}
      </p>
      <p v-if="errorMessage" class="status error" role="alert">
        {{ errorMessage }}
      </p>

      <router-link to="/login" class="login-link">ログインページへ戻る</router-link>
    </section>
  </main>
</template>

<script setup>
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import {
  confirmAccountRecovery,
  requestAccountRecovery,
} from "@/api/auth.js";

const route = useRoute();
const email = ref("");
const isSubmitting = ref(false);
const isComplete = ref(false);
const successMessage = ref("");
const errorMessage = ref("");
const recoveryToken = computed(() =>
  typeof route.query.token === "string" ? route.query.token : "",
);
const hasRecoveryToken = computed(() => recoveryToken.value.length > 0);

const clearMessages = () => {
  successMessage.value = "";
  errorMessage.value = "";
};

const requestRecovery = async () => {
  if (isSubmitting.value) return;
  clearMessages();
  isSubmitting.value = true;
  try {
    await requestAccountRecovery(email.value);
    successMessage.value =
      "復元可能なアカウントがある場合、確認メールを送信しました。";
    email.value = "";
  } catch {
    errorMessage.value =
      "メールを送信できませんでした。時間をおいて再度お試しください。";
  } finally {
    isSubmitting.value = false;
  }
};

const restoreAccount = async () => {
  if (isSubmitting.value || isComplete.value) return;
  clearMessages();
  isSubmitting.value = true;
  try {
    await confirmAccountRecovery(recoveryToken.value);
    isComplete.value = true;
    successMessage.value =
      "アカウントを復元しました。ログインページからログインしてください。";
  } catch {
    errorMessage.value =
      "復元リンクが無効か、有効期限が切れています。もう一度復元メールを申請してください。";
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
.account-recovery-page {
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: var(--spacing-xl) var(--spacing-md);
  background: linear-gradient(
    135deg,
    var(--color-background),
    var(--color-primary-light)
  );
}

.recovery-card {
  width: min(100%, 30rem);
  padding: var(--spacing-xl);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-xl);
  background: var(--color-surface);
  box-shadow: var(--shadow-lg);
}

.brand-link {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-lg);
  color: var(--color-text);
  font-size: 1.125rem;
  font-weight: 700;
  text-decoration: none;
}

.brand-link img {
  width: 2rem;
  height: 2rem;
}

h1 {
  margin: 0 0 var(--spacing-sm);
  color: var(--color-text);
  font-size: 1.75rem;
}

.description {
  margin: 0 0 var(--spacing-lg);
  color: var(--color-text-muted);
  line-height: 1.6;
}

form {
  display: grid;
  gap: var(--spacing-sm);
}

label {
  color: var(--color-text);
  font-weight: 600;
}

input {
  width: 100%;
  min-height: 2.75rem;
  padding: 0 var(--spacing-md);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text);
  background: var(--color-surface);
}

input:focus-visible,
.primary-button:focus-visible,
.login-link:focus-visible,
.brand-link:focus-visible {
  outline: 3px solid var(--color-primary-light);
  outline-offset: 2px;
}

.primary-button {
  width: 100%;
  min-height: 2.75rem;
  margin-top: var(--spacing-sm);
  border: 0;
  border-radius: var(--radius-md);
  color: var(--color-surface);
  background: var(--color-primary);
  font-weight: 600;
  cursor: pointer;
}

.primary-button:hover:not(:disabled) {
  background: var(--color-primary-hover);
}

.primary-button:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.status {
  margin: var(--spacing-md) 0 0;
  padding: var(--spacing-md);
  border-radius: var(--radius-md);
}

.success {
  color: #166534;
  background: var(--color-success-light);
}

.error {
  color: #991b1b;
  background: var(--color-danger-light);
}

.login-link {
  display: inline-block;
  margin-top: var(--spacing-lg);
  color: var(--color-primary);
  font-weight: 600;
}

@media (max-width: 600px) {
  .account-recovery-page {
    padding: var(--spacing-md);
  }

  .recovery-card {
    padding: var(--spacing-lg);
  }
}
</style>
